import {
  ANALYSIS_VISUALIZATION_TYPE_TO_ICON,
  ANALYSIS_VISUALIZATION_TYPE_TO_READABLE,
} from 'analysis-tool/charting/AnalysisChartingConstants';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {assert} from 'Asserts';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getStepTypeIcon} from 'pipelines/StepIconUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

import {AnalysisFlowchartMode} from '../AnalysisFlowchartContext';

interface StepNodeContents {
  nodeName: string;
  nodeIcon?: BenchlingIconName;
  nodeSecondaryInfo?: string;
  nodeStatus?: BenchlingNodeV2Status;
}

const STEP_STATUS_CODE_TO_NODE_STATUS: Partial<Record<PipelineStepStatusCode, BenchlingNodeV2Status>> = {
  [PipelineStepStatusCode.FAILED]: 'error',
  [PipelineStepStatusCode.WAITING]: 'warning',
  [PipelineStepStatusCode.RUNNING]: 'in-progress',
  [PipelineStepStatusCode.SUCCEEDED]: 'success',
  [PipelineStepStatusCode.BLOCKED]: 'pending',
  [PipelineStepStatusCode.AI_PROPOSED]: 'ai-proposed',
  [PipelineStepStatusCode.HALTED]: 'pending',
};

const STEP_TYPE_TO_NODE_CONTENTS_FN: Partial<
  Record<PipelineStepType, (step: PipelineStep) => Partial<StepNodeContents>>
> = {
  [PipelineStepType.CHART_VIEW]: (step: PipelineStep<PipelineStepType.CHART_VIEW, 0>) => {
    const visualizationType = step.config?.chartConfig?.type;
    assert(visualizationType != null, 'Expected visualization type to be set for custom node content');

    return {
      nodeIcon: ANALYSIS_VISUALIZATION_TYPE_TO_ICON[visualizationType as AnalysisVisualizationType],
      nodeName: ANALYSIS_VISUALIZATION_TYPE_TO_READABLE[visualizationType as AnalysisVisualizationType],
    };
  },
  [PipelineStepType.TABLE_VIEW]: (step: PipelineStep<PipelineStepType.TABLE_VIEW, 0>) => ({
    nodeIcon: 'table',
    nodeName: step.config?.viewName ?? 'Table view',
  }),
};

function isSourceNode(step: PipelineStep): boolean {
  return (
    step.inputStepDatas.length === 0 &&
    step.outputStepDatas.length === 1 &&
    step.outputStepDatas[0].rawDatas.length === 1
  );
}

function getNonDefaultContent(step: PipelineStep): Partial<StepNodeContents> {
  if (step.config != null && STEP_TYPE_TO_NODE_CONTENTS_FN[step.stepType] != null) {
    return STEP_TYPE_TO_NODE_CONTENTS_FN[step.stepType]!(step);
  }

  // If a source step has a single output, show the name of the output
  if (isSourceNode(step)) {
    return {
      nodeName: step.outputStepDatas[0].rawDatas[0].name,
    };
  }

  return {};
}

/**
 * Create step type specific contents for a node, including the node's title, icon, and status.
 *
 * To update a step type's icon, add an `icon` field to the step type's `yaml` declaration.
 *
 * To update a step type's user-facing name, update `name` in the step type's `yaml` declaration.
 *
 * To dynamically change a node's name or icon based on its config or execution state, add a function
 * to `STEP_TYPE_TO_NODE_CONTENTS_FN`.
 */
export default function getStepNodeContents(
  step: PipelineStep,
  mode: AnalysisFlowchartMode
): StepNodeContents {
  const statusProperties: Partial<StepNodeContents> =
    mode === AnalysisFlowchartMode.STATIC
      ? {}
      : {
          nodeStatus: STEP_STATUS_CODE_TO_NODE_STATUS[step.statusCode],
          nodeSecondaryInfo: step.statusMessage != null ? step.statusMessage : undefined,
        };

  return {
    nodeName: STEP_TYPE_TO_USER_FACING_NAME[step.stepType],
    nodeIcon: getStepTypeIcon(step.stepType) ?? undefined,
    ...statusProperties,
    ...getNonDefaultContent(step),
  };
}
